import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl, API_ENDPOINTS } from '../../config/api';
import './LeadManagement.css';

const LeadManagement = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Modal states
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [modalNotesData, setModalNotesData] = useState(null);
  const [modalEditMode, setModalEditMode] = useState(false);
  const [tempNotes, setTempNotes] = useState('');
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newLeadData, setNewLeadData] = useState({ name: '', email: '', phone: '', notes: '' });
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedLeadForCustomer, setSelectedLeadForCustomer] = useState(null);
  const [isClosedWinMode, setIsClosedWinMode] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    hasKids: false,
    kidsCount: '',
    kidsAges: '',
    currentInsurance: '',
    desiredCoverage: '',
    budgetRange: '',
    medicalHistory: '',
    medications: '',
    preferredDoctor: '',
    insuranceType: 'individual',
    employmentStatus: '',
    annualIncome: '',
    policyStartDate: '',
    notes: '',
    leadId: ''
  });

  // Filter states
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    phone: '',
    dateFrom: '',
    dateTo: '',
    specificDate: '',
    status: '',
    claimedBy: ''
  });

  const navigate = useNavigate();

  const token = localStorage.getItem('token');

  const fetchLeads = useCallback(async () => {
    try {
      const headers = {
        Authorization: `Bearer ${token}`
      };

      const response = await axios.get(getApiUrl(API_ENDPOINTS.adminLeads), { headers });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  const applyFilters = useCallback(() => {
    let filtered = [...leads];

    // Apply tab filter first
    if (activeTab === 'ben') {
      filtered = filtered.filter(lead => 
        (lead.claimedBy === 'Ben' || lead.claimedBy === 'Ben Fishman') && lead.status !== 'lost'
      );
    } else if (activeTab === 'stef') {
      filtered = filtered.filter(lead => 
        (lead.claimedBy === 'Stef' || lead.claimedBy === 'Stefanie' || lead.claimedBy === 'Stefanie Fishman') && lead.status !== 'lost'
      );
    } else if (activeTab === 'unclaimed') {
      filtered = filtered.filter(lead => !lead.claimedBy && lead.status !== 'lost');
    } else if (activeTab === 'claimed') {
      filtered = filtered.filter(lead => lead.claimedBy && lead.status !== 'lost');
    } else if (activeTab === 'closed') {
      filtered = filtered.filter(lead => lead.status === 'closed');
    } else if (activeTab === 'lost') {
      filtered = filtered.filter(lead => lead.status === 'lost');
    }
    // 'all' tab shows everything

    // Apply text filters
    if (filters.name) {
      filtered = filtered.filter(lead => 
        lead.name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    if (filters.email) {
      filtered = filtered.filter(lead => 
        lead.email.toLowerCase().includes(filters.email.toLowerCase())
      );
    }

    if (filters.phone) {
      filtered = filtered.filter(lead => 
        lead.phone.replace(/\D/g, '').includes(filters.phone.replace(/\D/g, ''))
      );
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(lead => 
        lead.status === filters.status
      );
    }

    // Apply claimed by filter
    if (filters.claimedBy) {
      filtered = filtered.filter(lead => 
        lead.claimedBy && lead.claimedBy.toLowerCase().includes(filters.claimedBy.toLowerCase())
      );
    }

    // Apply date filters
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(lead => 
        new Date(lead.createdAt) >= fromDate
      );
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(lead => 
        new Date(lead.createdAt) <= toDate
      );
    }

    if (filters.specificDate) {
      const specificDate = new Date(filters.specificDate);
      const nextDay = new Date(specificDate);
      nextDay.setDate(nextDay.getDate() + 1);
      
      filtered = filtered.filter(lead => {
        const leadDate = new Date(lead.createdAt);
        return leadDate >= specificDate && leadDate < nextDay;
      });
    }

    setFilteredLeads(filtered);
  }, [leads, filters, activeTab]);

  useEffect(() => {
    // Check if user is logged in
    if (!token) {
      navigate('/admin');
      return;
    }

    fetchLeads();
  }, [token, navigate, fetchLeads]);

  useEffect(() => {
    // Apply filters whenever leads or filters change
    applyFilters();
  }, [applyFilters]);





  const updateLeadStatus = async (leadId, newStatus) => {
    // If status is 'closed', show customer information modal
    if (newStatus === 'closed') {
      const lead = leads.find(l => l.leadId === leadId);
      if (lead) {
        setSelectedLeadForCustomer(lead);
        setCustomerForm(prev => ({
          ...prev,
          name: lead.name,
          email: lead.email,
          phone: lead.phone,
          leadId: lead.leadId
        }));
        setShowCustomerModal(true);
        return; // Don't update status yet, wait for customer form completion
      }
    }

    try {
      await axios.put(
        getApiUrl(`${API_ENDPOINTS.adminUpdateLead}/${leadId}`),
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchLeads();
    } catch (error) {
      console.error('Error updating lead status:', error);
      alert('Error updating lead status. Please try again.');
    }
  };

  const updateLeadNotes = async (leadId, notes) => {
    try {
      await axios.put(
        getApiUrl(`${API_ENDPOINTS.adminUpdateLead}/${leadId}`),
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchLeads();
    } catch (error) {
      console.error('Error updating lead notes:', error);
      alert('Error updating notes. Please try again.');
    }
  };

  const claimLead = async (leadId, assignTo) => {
    try {
      console.log('Claiming lead:', leadId, 'for:', assignTo);
      const response = await axios.put(
        getApiUrl(`${API_ENDPOINTS.adminUpdateLead}/${leadId}/claim`),
        { claimedBy: assignTo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Claim response:', response.data);
      await fetchLeads();
    } catch (error) {
      console.error('Error claiming lead:', error);
      console.error('Error response:', error.response?.data);
      alert('Error claiming lead. Please try again.');
    }
  };

  const unclaimLead = async (leadId) => {
    try {
      console.log('Unclaiming lead:', leadId);
      const response = await axios.put(
        getApiUrl(`${API_ENDPOINTS.adminUpdateLead}/${leadId}/unclaim`),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Unclaim response:', response.data);
      await fetchLeads();
    } catch (error) {
      console.error('Error unclaiming lead:', error);
      console.error('Error response:', error.response?.data);
      alert('Error unclaiming lead. Please try again.');
    }
  };

  const deleteLead = async (leadId, leadName) => {
    if (window.confirm(`Are you sure you want to permanently delete the lead for ${leadName}?`)) {
      try {
        await axios.delete(
          getApiUrl(`${API_ENDPOINTS.adminUpdateLead}/${leadId}`),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchLeads();
      } catch (error) {
        console.error('Error deleting lead:', error);
        alert('Error deleting lead. Please try again.');
      }
    }
  };

  const createNewLead = async () => {
    try {
      const response = await axios.post(
        getApiUrl(API_ENDPOINTS.adminLeads),
        newLeadData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.status === 201) {
        setShowCreateModal(false);
        setNewLeadData({ name: '', email: '', phone: '', notes: '' });
        fetchLeads();
      }
    } catch (error) {
      console.error('Error creating lead:', error);
      alert('Error creating lead. Please try again.');
    }
  };

  // Bulk operations
  const handleSelectLead = (leadId) => {
    const newSelected = new Set(selectedLeads);
    if (newSelected.has(leadId)) {
      newSelected.delete(leadId);
    } else {
      newSelected.add(leadId);
    }
    setSelectedLeads(newSelected);
    setSelectAll(newSelected.size === filteredLeads.length && filteredLeads.length > 0);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedLeads(new Set());
      setSelectAll(false);
    } else {
      const allLeadIds = new Set(filteredLeads.map(lead => lead.leadId));
      setSelectedLeads(allLeadIds);
      setSelectAll(true);
    }
  };

  const bulkDelete = async () => {
    if (window.confirm(`Are you sure you want to permanently delete ${selectedLeads.size} selected leads?`)) {
      try {
        const promises = Array.from(selectedLeads).map(leadId => 
          axios.delete(
            getApiUrl(`${API_ENDPOINTS.adminUpdateLead}/${leadId}`),
            { headers: { Authorization: `Bearer ${token}` } }
          )
        );
        await Promise.all(promises);
        setSelectedLeads(new Set());
        setSelectAll(false);
        fetchLeads();
      } catch (error) {
        console.error('Error bulk deleting leads:', error);
        alert('Error deleting some leads. Please try again.');
      }
    }
  };

  const bulkClaim = async (assignTo) => {
    try {
      const promises = Array.from(selectedLeads).map(leadId => 
        axios.put(
          getApiUrl(`${API_ENDPOINTS.adminUpdateLead}/${leadId}/claim`),
          { claimedBy: assignTo },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );
      await Promise.all(promises);
      setSelectedLeads(new Set());
      setSelectAll(false);
      fetchLeads();
    } catch (error) {
      console.error('Error bulk claiming leads:', error);
      alert('Error claiming some leads. Please try again.');
    }
  };

  const bulkStatusUpdate = async (newStatus) => {
    try {
      const promises = Array.from(selectedLeads).map(leadId => 
        axios.put(
          getApiUrl(`${API_ENDPOINTS.adminUpdateLead}/${leadId}`),
          { status: newStatus },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );
      await Promise.all(promises);
      setSelectedLeads(new Set());
      setSelectAll(false);
      fetchLeads();
    } catch (error) {
      console.error('Error bulk updating status:', error);
      alert('Error updating some leads. Please try again.');
    }
  };

  const bulkUnclaim = async () => {
    try {
      const promises = Array.from(selectedLeads).map(leadId => 
        axios.put(
          getApiUrl(`${API_ENDPOINTS.adminUpdateLead}/${leadId}/unclaim`),
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );
      await Promise.all(promises);
      setSelectedLeads(new Set());
      setSelectAll(false);
      fetchLeads();
    } catch (error) {
      console.error('Error bulk unclaiming leads:', error);
      alert('Error unclaiming some leads. Please try again.');
    }
  };



  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Modal functions for notes
  const openNotesModal = (lead) => {
    setModalNotesData(lead);
    setTempNotes(lead.notes || '');
    setShowNotesModal(true);
    setModalEditMode(false);
    setNotesExpanded(false);
  };

  const closeNotesModal = () => {
    setShowNotesModal(false);
    setModalNotesData(null);
    setModalEditMode(false);
    setTempNotes('');
    setNotesExpanded(false);
  };

  const startModalEdit = () => {
    setModalEditMode(true);
  };

  const cancelModalEdit = () => {
    setModalEditMode(false);
    setTempNotes(modalNotesData?.notes || '');
  };

  const saveModalNotes = async () => {
    if (modalNotesData) {
      try {
        await updateLeadNotes(modalNotesData.leadId, tempNotes);
        setModalEditMode(false);
        setModalNotesData({ ...modalNotesData, notes: tempNotes });
      } catch (error) {
        console.error('Error saving notes:', error);
      }
    }
  };

  const handleCreateLeadChange = (e) => {
    const { name, value } = e.target;
    setNewLeadData(prev => ({ ...prev, [name]: value }));
  };

  // Customer form handlers
  const handleCustomerInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCustomerForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isClosedWinMode) {
        // Convert to customer and close lead
        const response = await axios.post(
          getApiUrl('/api/admin/customers'),
          customerForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.status === 200 || response.status === 201) {
          // Update the lead status to closed
          await axios.put(
            getApiUrl(`${API_ENDPOINTS.adminUpdateLead}/${selectedLeadForCustomer.leadId}`),
            { status: 'closed' },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          // Close modal and refresh leads
          setShowCustomerModal(false);
          setSelectedLeadForCustomer(null);
          setIsClosedWinMode(false);
          setCustomerForm({
            name: '', email: '', phone: '', age: '', hasKids: false,
            kidsCount: '', kidsAges: '', currentInsurance: '', desiredCoverage: '',
            budgetRange: '', medicalHistory: '', medications: '', preferredDoctor: '',
            insuranceType: 'individual', employmentStatus: '', annualIncome: '',
            policyStartDate: '', notes: '', leadId: ''
          });
          await fetchLeads();
          alert('Customer added successfully and lead marked as closed!');
        }
      } else {
        // Just save profile data to lead
        await axios.put(
          getApiUrl(`${API_ENDPOINTS.adminUpdateLead}/${selectedLeadForCustomer.leadId}`),
          { 
            notes: customerForm.notes,
            // Add any other profile fields you want to save to the lead
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Close modal and refresh leads
        setShowCustomerModal(false);
        setSelectedLeadForCustomer(null);
        setIsClosedWinMode(false);
        setCustomerForm({
          name: '', email: '', phone: '', age: '', hasKids: false,
          kidsCount: '', kidsAges: '', currentInsurance: '', desiredCoverage: '',
          budgetRange: '', medicalHistory: '', medications: '', preferredDoctor: '',
          insuranceType: 'individual', employmentStatus: '', annualIncome: '',
          policyStartDate: '', notes: '', leadId: ''
        });
        await fetchLeads();
        alert('Profile saved successfully!');
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      alert('Error saving information. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="lead-management-loading">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading leads...</p>
      </div>
    );
  }

  return (
    <div className="lead-management-container">
      {/* Lead Management Header */}
      <div className="lead-management-header">
        <h1 className="lead-management-title">Lead Management</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="add-lead-btn"
        >
          ➕ Add New Lead
        </button>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <div className="search-container">
          <span className="search-icon">⌕</span>
          <input
            type="text"
            className="search-input"
            placeholder="Search leads by name..."
            value={filters.name}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, name: e.target.value }));
            }}
          />
        </div>
      </div>

      {/* Tabs Section */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All Leads ({leads.length})
        </button>
        <button 
          className={`tab ${activeTab === 'unclaimed' ? 'active' : ''}`}
          onClick={() => setActiveTab('unclaimed')}
        >
          Unclaimed ({leads.filter(l => !l.claimedBy && l.status !== 'lost').length})
        </button>
        <button 
          className={`tab ${activeTab === 'ben' ? 'active' : ''}`}
          onClick={() => setActiveTab('ben')}
        >
          Ben's Leads ({leads.filter(l => (l.claimedBy === 'Ben' || l.claimedBy === 'Ben Fishman') && l.status !== 'lost').length})
        </button>
        <button 
          className={`tab ${activeTab === 'stef' ? 'active' : ''}`}
          onClick={() => setActiveTab('stef')}
        >
          Stef's Leads ({leads.filter(l => (l.claimedBy === 'Stef' || l.claimedBy === 'Stefanie' || l.claimedBy === 'Stefanie Fishman') && l.status !== 'lost').length})
        </button>

        <button 
          className={`tab ${activeTab === 'lost' ? 'active' : ''}`}
          onClick={() => setActiveTab('lost')}
        >
          Lost ({leads.filter(l => l.status === 'lost').length})
        </button>
      </div>

      {/* Bulk Actions */}
      {selectedLeads.size > 0 && (
        <div className="bulk-actions">
          <div className="bulk-info">
            <div className="selected-count">{selectedLeads.size} selected</div>
          </div>
          <div className="bulk-buttons">
            <button
              onClick={() => bulkClaim('Ben')}
              className="bulk-btn claim-ben"
            >
              Assign to Ben
            </button>
            <button
              onClick={() => bulkClaim('Stef')}
              className="bulk-btn claim-stef"
            >
              Assign to Stef
            </button>
            <button
              onClick={bulkUnclaim}
              className="bulk-btn unclaim"
            >
              Unclaim
            </button>
            <button
              onClick={() => bulkStatusUpdate('lost')}
              className="bulk-btn status-lost"
            >
              Lost
            </button>
            <button
              onClick={bulkDelete}
              className="bulk-btn delete"
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Table Container */}
      <div className="table-container">
        <table className="leads-table">
          <thead>
            <tr>
              <th className="select-header">
                <input
                  type="checkbox"
                  className="select-checkbox"
                  checked={selectAll}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectAll();
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </th>
              <th>Name</th>
              <th>Contact</th>
              <th>Submitted</th>
                              <th>Assigned</th>
              <th>Profile</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeads.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  {activeTab === 'all' && 'No leads found'}
                  {activeTab === 'unclaimed' && 'No unclaimed leads'}
                  {activeTab === 'claimed' && 'No claimed leads'}
                  {activeTab === 'lost' && 'No lost leads'}
                </td>
              </tr>
            ) : (
              filteredLeads.map(lead => (
                <tr 
                  key={lead.leadId} 
                  className={`lead-row ${lead.status === 'lost' ? 'lost-lead' : ''}`}
                  onClick={() => {
                    setSelectedLeadForCustomer(lead);
                    setCustomerForm({
                      name: lead.name || '',
                      email: lead.email || '',
                      phone: lead.phone || '',
                      age: '',
                      hasKids: false,
                      kidsCount: '',
                      kidsAges: '',
                      currentInsurance: '',
                      desiredCoverage: '',
                      budgetRange: '',
                      medicalHistory: '',
                      medications: '',
                      preferredDoctor: '',
                      insuranceType: 'individual',
                      employmentStatus: '',
                      annualIncome: '',
                      policyStartDate: '',
                      notes: lead.notes || '',
                      leadId: lead.leadId
                    });
                    setShowCustomerModal(true);
                  }}
                >
                  <td className="select-cell">
                    <input
                      type="checkbox"
                      className="select-checkbox"
                      checked={selectedLeads.has(lead.leadId)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectLead(lead.leadId);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className="name-cell">{lead.name}</td>
                  <td>
                    <div className="email-cell">{lead.email}</div>
                    <div className="phone-cell">{lead.phone}</div>
                  </td>
                  <td className="date-cell">
                    {formatDate(lead.timestamp)}
                  </td>
                  <td className="status-cell">
                    <div className="status-display">
                      {lead.status === 'closed' && <span className="status-badge closed">Closed</span>}
                      {lead.status === 'lost' && <span className="status-badge lost">Lost</span>}
                      {lead.status !== 'closed' && lead.status !== 'lost' && (
                        lead.claimedBy ? (
                          <span className="status-badge claimed">{lead.claimedBy}</span>
                        ) : (
                          <span className="status-badge unclaimed">Unclaimed</span>
                        )
                      )}
                    </div>
                  </td>
                  <td className="profile-cell">
                    <span className="profile-indicator">📋</span>
                  </td>
                  <td className="delete-cell">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteLead(lead.leadId, lead.name);
                      }}
                      className="delete-btn"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Lead Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>➕ Create New Lead</h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={newLeadData.name}
                  onChange={handleCreateLeadChange}
                  placeholder="Enter full name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={newLeadData.email}
                  onChange={handleCreateLeadChange}
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={newLeadData.phone}
                  onChange={handleCreateLeadChange}
                  placeholder="Enter phone number"
                  required
                />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={newLeadData.notes}
                  onChange={handleCreateLeadChange}
                  placeholder="Add any initial notes..."
                  rows="4"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={createNewLead}
                disabled={!newLeadData.name || !newLeadData.email || !newLeadData.phone}
              >
                Create Lead
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && modalNotesData && (
        <div className="modal-overlay" onClick={closeNotesModal}>
          <div className="modal-content notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📝 Notes for {modalNotesData.name}</h3>
              <button className="modal-close" onClick={closeNotesModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="lead-info">
                <p><strong>Email:</strong> {modalNotesData.email}</p>
                <p><strong>Phone:</strong> {modalNotesData.phone}</p>
                <p><strong>Date:</strong> {formatDate(modalNotesData.timestamp)}</p>
              </div>
              <div className="notes-section">
                <label>Notes</label>
                {modalEditMode ? (
                  <textarea
                    value={tempNotes}
                    onChange={(e) => setTempNotes(e.target.value)}
                    rows="8"
                    placeholder="Add your notes here..."
                    className="notes-textarea"
                  />
                ) : (
                  <div className="notes-display">
                    {modalNotesData.notes ? (
                      <>
                        <div className={`notes-content ${notesExpanded ? 'expanded' : 'collapsed'}`}>
                          {modalNotesData.notes}
                        </div>
                        {modalNotesData.notes.length > 200 && (
                          <button 
                            className="expand-notes-btn"
                            onClick={() => setNotesExpanded(!notesExpanded)}
                          >
                            {notesExpanded ? '📄 Show Less' : '📖 Read More'}
                          </button>
                        )}
                      </>
                    ) : (
                      'No notes added yet.'
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              {modalEditMode ? (
                <>
                  <button className="btn-secondary" onClick={cancelModalEdit}>
                    Cancel
                  </button>
                  <button className="btn-primary" onClick={saveModalNotes}>
                    Save Notes
                  </button>
                </>
              ) : (
                <>
                  <button className="btn-secondary" onClick={closeNotesModal}>
                    Close
                  </button>
                  <button className="btn-primary" onClick={startModalEdit}>
                    Edit Notes
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Customer Information Modal */}
      {showCustomerModal && selectedLeadForCustomer && (
        <div className="modal-overlay" onClick={() => setShowCustomerModal(false)}>
          <div className="modal-content customer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {isClosedWinMode ? '🏆 Closed Win - Convert to Customer' : '📝 Profile Information'} 
                for {selectedLeadForCustomer.name}
              </h3>
              <button className="modal-close" onClick={() => {
                setShowCustomerModal(false);
                setIsClosedWinMode(false);
              }}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCustomerSubmit}>
                <div className="form-group">
                  <label>Name {isClosedWinMode && '*'}</label>
                  <input
                    type="text"
                    name="name"
                    value={customerForm.name}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter full name"
                    required={isClosedWinMode}
                  />
                </div>
                <div className="form-group">
                  <label>Email {isClosedWinMode && '*'}</label>
                  <input
                    type="email"
                    name="email"
                    value={customerForm.email}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter email address"
                    required={isClosedWinMode}
                  />
                </div>
                <div className="form-group">
                  <label>Phone {isClosedWinMode && '*'}</label>
                  <input
                    type="tel"
                    name="phone"
                    value={customerForm.phone}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter phone number"
                    required={isClosedWinMode}
                  />
                </div>
                <div className="form-group">
                  <label>Age</label>
                  <input
                    type="number"
                    name="age"
                    value={customerForm.age}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter age"
                  />
                </div>
                <div className="form-group">
                  <label>Has Kids</label>
                  <input
                    type="checkbox"
                    name="hasKids"
                    checked={customerForm.hasKids}
                    onChange={handleCustomerInputChange}
                  />
                </div>
                {customerForm.hasKids && (
                  <>
                    <div className="form-group">
                      <label>Kids Count</label>
                      <input
                        type="number"
                        name="kidsCount"
                        value={customerForm.kidsCount}
                        onChange={handleCustomerInputChange}
                        placeholder="Enter number of kids"
                      />
                    </div>
                    <div className="form-group">
                      <label>Kids Ages (comma-separated)</label>
                      <input
                        type="text"
                        name="kidsAges"
                        value={customerForm.kidsAges}
                        onChange={handleCustomerInputChange}
                        placeholder="e.g., 5, 8, 12"
                      />
                    </div>
                  </>
                )}
                <div className="form-group">
                  <label>Current Insurance</label>
                  <input
                    type="text"
                    name="currentInsurance"
                    value={customerForm.currentInsurance}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter current insurance details"
                  />
                </div>
                <div className="form-group">
                  <label>Desired Coverage</label>
                  <input
                    type="text"
                    name="desiredCoverage"
                    value={customerForm.desiredCoverage}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter desired coverage"
                  />
                </div>
                <div className="form-group">
                  <label>Budget Range</label>
                  <input
                    type="text"
                    name="budgetRange"
                    value={customerForm.budgetRange}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter budget range"
                  />
                </div>
                <div className="form-group">
                  <label>Medical History</label>
                  <textarea
                    name="medicalHistory"
                    value={customerForm.medicalHistory}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter medical history"
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label>Medications</label>
                  <textarea
                    name="medications"
                    value={customerForm.medications}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter medications"
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label>Preferred Doctor</label>
                  <input
                    type="text"
                    name="preferredDoctor"
                    value={customerForm.preferredDoctor}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter preferred doctor"
                  />
                </div>
                <div className="form-group">
                  <label>Insurance Type</label>
                  <select
                    name="insuranceType"
                    value={customerForm.insuranceType}
                    onChange={handleCustomerInputChange}
                  >
                    <option value="individual">Individual</option>
                    <option value="family">Family</option>
                    <option value="group">Group</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Employment Status</label>
                  <input
                    type="text"
                    name="employmentStatus"
                    value={customerForm.employmentStatus}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter employment status"
                  />
                </div>
                <div className="form-group">
                  <label>Annual Income</label>
                  <input
                    type="number"
                    name="annualIncome"
                    value={customerForm.annualIncome}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter annual income"
                  />
                </div>
                <div className="form-group">
                  <label>Policy Start Date</label>
                  <input
                    type="date"
                    name="policyStartDate"
                    value={customerForm.policyStartDate}
                    onChange={handleCustomerInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    name="notes"
                    value={customerForm.notes}
                    onChange={handleCustomerInputChange}
                    placeholder="Add any notes about the customer"
                    rows="3"
                  />
                </div>
                <div className="modal-footer">
                  <button className="btn-secondary" onClick={() => {
                    setShowCustomerModal(false);
                    setIsClosedWinMode(false);
                  }}>
                    Cancel
                  </button>
                  {!isClosedWinMode && (
                    <button 
                      className="btn-warning" 
                      type="button"
                      onClick={() => setIsClosedWinMode(true)}
                    >
                      🏆 Closed Win
                    </button>
                  )}
                  <button 
                    className="btn-primary" 
                    type="submit"
                  >
                    {isClosedWinMode ? 'Convert to Customer' : 'Save Profile'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadManagement; 